import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { type ReactNode } from 'react';
import { Alert } from '@thegpvc/ui';
import { api } from '../api/client';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { SectionCard } from '../components/SectionCard';
import { text, bg, border, cx } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';
import { setupPollInterval } from '../utils/setupPolling';
import { installCommand } from '../utils/installCommand';

/** A shell command in a mono block with a copy button in the corner. */
function CommandBlock({ command, label }: { command: string; label: string }) {
  return (
    <div
      className={cx(
        'flex items-center gap-2 rounded-lg border p-3 font-mono text-sm',
        bg.surface,
        border.default,
        text.primary,
      )}
    >
      <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all">{command}</pre>
      <CopyButton value={command} label={label} />
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <SectionCard
      title={
        <span>
          Step {n} · {title}
        </span>
      }
      bodyClassName="space-y-3"
    >
      {children}
    </SectionCard>
  );
}

/**
 * Setup wizard — walks a brand-new user from an empty account to their first
 * synced session in three steps: install the daemon, authenticate it, then wait
 * for the first upload. The Verify step polls GetSyncStatus and stops the moment
 * a file arrives (setupPollInterval), flipping to a success state that shows the
 * real file count — it never renders success on zero files.
 */
export function Setup() {
  const sync = useQuery({
    queryKey: ['syncStatus'],
    queryFn: () => api.getSyncStatus(),
    // Functional interval: poll every few seconds until the first file arrives,
    // then return false so polling stops when settled (async-lifecycle rule).
    refetchInterval: (query) => setupPollInterval(query.state.data),
  });

  // int64 fields arrive as strings and are omitted when zero — default the
  // absent case. Success is gated strictly on a real positive count.
  const totalFiles = parseInt(sync.data?.totalFiles ?? '0', 10);
  const connected = totalFiles > 0;

  return (
    <PageContainer width="narrow">
      <title>Dixie :: Setup</title>
      <PageHeader title="Set up Dixie" />

      <p className={text.secondary}>
        Dixie learns from your Claude Code sessions. A small background daemon on
        your machine watches Claude Code's session logs and uploads them here —
        this dashboard fills in automatically once it's running. It takes about a
        minute to set up.
      </p>

      <Step n={1} title="Install the daemon">
        <p className={text.secondary}>
          Run this in your terminal. It installs a small background daemon that
          uploads your Claude Code session logs to Dixie.
        </p>
        <CommandBlock command={installCommand()} label="Copy install command" />
      </Step>

      <Step n={2} title="Authenticate">
        <p className={text.secondary}>
          Then authenticate the daemon. This opens a browser to sign in with the
          same Google account you used here, and starts the daemon watching your
          logs.
        </p>
        <CommandBlock command="dixie setup" label="Copy setup command" />
      </Step>

      <Step n={3} title="Verify">
        {sync.isError ? (
          <Alert variant="error">
            Couldn't check sync status: {errorMessage(sync.error)}
          </Alert>
        ) : connected ? (
          <Alert variant="success">
            <div className="space-y-2">
              <p>
                Connected — Dixie has received{' '}
                <span className="font-semibold">{totalFiles.toLocaleString()}</span>{' '}
                {totalFiles === 1 ? 'file' : 'files'} from your daemon.
              </p>
              <Link to="/" className={text.link}>
                Go to your dashboard →
              </Link>
            </div>
          </Alert>
        ) : (
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-b-2 border-orange-500" />
            <p className={text.secondary}>
              Waiting for your first session to arrive… Leave this page open; it
              updates automatically once the daemon uploads.
            </p>
          </div>
        )}
      </Step>
    </PageContainer>
  );
}
